import { EventEmitter, inject,Injectable } from '@angular/core';
import { DpModalService, HeaderAction } from '@docpath/webcomponents';
import { faMagnifyingGlass, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { StatisticsHttpService } from '../../../../statistics/services/statistics-http.service';
import { Alias } from '../../../alias/models/alias';
import { AliasHttpService } from '../../../alias/services/alias-http.service';
import { Dge } from '../../../engines/models/dge';
import { Notification } from '../../models/notification';
import { NotificationOptions } from '../../models/notification-options';
import { FailedJobsService } from '../../services/failed-jobs.service';
import { FailedJobsFormService } from '../../services/failed-jobs-form.service';
import { FailedJobsHttpService } from '../../services/failed-jobs-http.service';

@Injectable({
    providedIn: 'root'
})
export class FailedJobsHeaderService {
    private readonly i18nPrefix = 'failed-jobs';

    aliasLoaded: EventEmitter<Alias[]> = new EventEmitter();
    dgesLoaded: EventEmitter<Dge[]> = new EventEmitter();

     
    private readonly failedJobsService = inject(FailedJobsService);
    private readonly failedJobsHttpService = inject(FailedJobsHttpService);
    private readonly failedJobsFormService = inject(FailedJobsFormService);
    private readonly modalService = inject(DpModalService);
    private readonly statisticsHttpService = inject(StatisticsHttpService);
    private readonly aliasHttpService = inject(AliasHttpService);

    getRightActions(notificationOptions: NotificationOptions): HeaderAction[] {
        return [
            this.initNewNotificationAction(notificationOptions),
            this.initSearchAction()
        ];
    }

    getSearchAction(actions: HeaderAction[]): HeaderAction {
        return actions[1];
    }

    getMoreActions(selectedNotifications: Notification[]): HeaderAction[] {
        const actions = [];

        if (selectedNotifications.length > 0) {
            actions.push(this.initDeleteAction(selectedNotifications));
        }

        return actions;
    }

    getLeftActions(selectedNotifications: Notification[], notificationOptions: NotificationOptions): HeaderAction[] {
        const actions = [];

        if (selectedNotifications.length === 1) {
            const notificationsSelected = selectedNotifications[0];
            actions.push(this.initEditAction(notificationsSelected, notificationOptions));
        }

        return actions;
    }

    loadAliasOptions(): void {
        this.aliasHttpService.getAllAlias(result => {
            this.aliasLoaded.emit(result);
        });
    }

    loadDgesOptions(): void {
        this.statisticsHttpService.getAllDge().subscribe(result => {
            this.dgesLoaded.emit(result);
        });
    }

    private initSearchAction(): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.search.label`, { cssClass: 'btn btn-dpsecondary' });
        action.tooltip = `${this.i18nPrefix}.header.search.tooltip`;
        action.icon = faMagnifyingGlass;

        return action;
    }

    private initNewNotificationAction(notificationOptions: NotificationOptions): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.new-notification.label`);

        action.icon = faPlusCircle;
        action.tooltip = `${this.i18nPrefix}.header.new-notification.tooltip`;
        action.action = () => this.failedJobsFormService.openCreateNotificationForm(notificationOptions);
        action.disabled = notificationOptions.alias.length === 0 || notificationOptions.dges.length === 0;

        return action;
    }

    private initDeleteAction(notifications: Notification[]): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.delete.label`);

        action.tooltip = `${this.i18nPrefix}.header.delete.tooltip`;
        action.action = () => this.deleteNotifications(notifications);

        return action;
    }

    private deleteNotifications(notificationsList: Notification[]): void {
        const notificationsIds = notificationsList.map(notificationElement => notificationElement.id),
            modal = this.modalService.openQuestion(`${this.i18nPrefix}.modal.delete-question`);

        modal.componentInstance.accepted.subscribe(() => {
            this.deleteNotificationsRequest(notificationsIds);
        });
    }

    private deleteNotificationsRequest(notificationsIds: number[]): void {
        this.failedJobsHttpService.deleteNotifications(notificationsIds, () => {
            this.failedJobsService.notifyFailedJobsChange();
        });
    }

    private initEditAction(notification: Notification, notificationOptions: NotificationOptions): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.edit.label`);

        action.tooltip = `${this.i18nPrefix}.header.edit.tooltip`;
        action.action = () => this.failedJobsFormService.openEditNotificationForm(notification, notificationOptions);

        return action;
    }


}
